/* Page Onboarding (/onboarding) — Factumis app.
   Destinée à un utilisateur connecté ET vérifié, mais SANS organisation active
   (la garde de routage de la Task 7 redirige ici dans ce cas). Un seul champ :
   le nom de l'entreprise. On crée l'organisation Better Auth puis on la marque
   active, et on entre dans l'app.

   Le plan par défaut (`conformite`) est posé côté serveur (defaultValue des
   champs additionnels de l'org) : rien à faire ici. Le raccordement effectif
   à la Plateforme Agréée (Seqino) se déclenche à la signature du mandat de
   représentation (écran /mandat, étape suivante), pas à cette création d'org.

   API Better Auth (vérifiée, 1.6.15) :
   - authClient.organization.create({ name, slug }) → { data, error } ;
     data = { id, name, slug, createdAt, members, ... }. `slug` est requis.
   - authClient.organization.setActive({ organizationId }) → { data, error }.
   - Erreur de collision de slug : code ORGANIZATION_SLUG_ALREADY_TAKEN (ou
     ORGANIZATION_ALREADY_EXISTS). On retente une fois avec un suffixe court
     aléatoire ; si ça échoue encore, message FR amical. */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { authClient } from '../lib/auth-client';
import { apiPost } from '../lib/api';
import { AuthLayout } from './AuthLayout';
import { CompanySearch, type Company } from '../components/CompanySearch';
import { Button, Field, Input } from '../components/ui';
import { Icon } from '../lib/icons';

/* Génère un slug URL-safe : minuscules, accents retirés, tout caractère non
   alphanumérique → tiret, tirets superflus regroupés et rognés. */
function slugify(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // retire les diacritiques
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/* Suffixe court aléatoire (alphanumérique) pour départager une collision. */
function randomSuffix(): string {
  return Math.random().toString(36).slice(2, 6);
}

/* Mappe les erreurs Better Auth vers un message FR utilisateur. */
function frenchMessage(error: { code?: string; status?: number }): string {
  if (
    error.code === 'ORGANIZATION_SLUG_ALREADY_TAKEN' ||
    error.code === 'ORGANIZATION_ALREADY_EXISTS'
  )
    return 'Ce nom est déjà utilisé, essayez-en un autre.';
  if (error.status === 429) return 'Trop de tentatives, réessayez dans un instant.';
  return 'Une erreur est survenue, réessayez.';
}

export default function Onboarding() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  // Société choisie dans l'annuaire (préremplit le nom et permet d'enregistrer
  // SIREN/adresse/TVA dès la création — l'utilisateur démarre ainsi en règle).
  const [company, setCompany] = useState<Company | null>(null);
  // Champs légaux requis pour provisionner le compte e-invoicing. Préremplis depuis
  // l'annuaire ; ÉDITABLES car une entreprise « non diffusible » a son adresse (et
  // parfois son nom) masquée → l'utilisateur les complète à la main.
  const [addr, setAddr] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [naf, setNaf] = useState('');
  // Champs e-reporting DGFiP (défauts adaptés à la cible indépendants/TPE).
  const [enterpriseSize, setEnterpriseSize] = useState<'micro' | 'pme' | 'eti' | 'ge'>('micro');
  const [typeOperation, setTypeOperation] = useState<'services' | 'goods' | 'mixed'>('services');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const busy = loading;

  function pickCompany(co: Company) {
    setCompany(co);
    // Non diffusible : nom/adresse masqués (vides). On préremplit ce qui est dispo
    // sans écraser une saisie existante par du vide ; le SIREN reste capturé.
    if (co.name) setName(co.name);
    setAddr(co.address ?? '');
    setPostalCode(co.postalCode ?? '');
    setCity(co.city ?? '');
    setNaf(co.nafCode ?? '');
  }

  // Provisioning possible si une société est choisie (SIREN) ET que les champs
  // légaux requis pour le raccordement à la Plateforme Agréée sont renseignés
  // (annuaire OU saisie manuelle).
  const canProvision =
    !!company &&
    addr.trim() !== '' &&
    postalCode.trim() !== '' &&
    city.trim() !== '' &&
    /^\d{2}$/.test(naf.trim());

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    setErr(null);

    const trimmed = name.trim();
    const base = slugify(trimmed);
    if (!trimmed || !base) {
      setErr("Saisissez le nom de votre entreprise.");
      return;
    }

    setLoading(true);

    // 1re tentative avec le slug dérivé du nom ; en cas de collision on
    // retente une fois avec un suffixe aléatoire.
    let { data, error } = await authClient.organization.create({
      name: trimmed,
      slug: base,
    });
    if (
      error &&
      (error.code === 'ORGANIZATION_SLUG_ALREADY_TAKEN' ||
        error.code === 'ORGANIZATION_ALREADY_EXISTS')
    ) {
      ({ data, error } = await authClient.organization.create({
        name: trimmed,
        slug: `${base}-${randomSuffix()}`,
      }));
    }

    if (error || !data) {
      setLoading(false);
      setErr(error ? frenchMessage(error) : 'Une erreur est survenue, réessayez.');
      return;
    }

    // Marque l'organisation fraîchement créée comme active. L'org existe déjà
    // (create a réussi) : en cas d'échec on retente UNE fois setActive avec le
    // même id — surtout pas de nouveau create (sinon doublon d'organisation).
    let { error: activeError } = await authClient.organization.setActive({
      organizationId: data.id,
    });
    if (activeError) {
      ({ error: activeError } = await authClient.organization.setActive({
        organizationId: data.id,
      }));
    }
    if (activeError) {
      setLoading(false);
      setErr("Votre espace a été créé mais n'a pas pu être activé. Réessayez dans un instant.");
      return;
    }

    // Si l'annuaire a fourni toutes les données requises, on enregistre les infos
    // légales et e-reporting de l'org (POST /provision, aucun appel réseau externe).
    // Best-effort : un échec ne bloque pas l'entrée (l'utilisateur complète/relance
    // dans les Paramètres). Le raccordement effectif à la Plateforme Agréée (Seqino)
    // se déclenche séparément, à la signature du mandat de représentation (étape
    // suivante du funnel, juste après).
    if (canProvision && company) {
      try {
        await apiPost('/api/organizations/provision', {
          name: trimmed,
          siren: company.siren,
          address: addr.trim(),
          city: city.trim(),
          postalCode: postalCode.trim(),
          nafCode: naf.trim(),
          enterpriseSize,
          typeOperation,
        });
      } catch {
        // ignoré volontairement : activable plus tard dans les Paramètres.
      }
    }

    setLoading(false);
    // Etape suivante du funnel : signature du mandat de representation (le
    // raccordement PA s'enclenche a la signature).
    navigate('/mandat');
  }

  return (
    <AuthLayout
      title="Votre entreprise"
      subtitle="Dernière étape : indiquez le nom de votre entreprise pour créer votre espace."
    >
      {err ? (
        <div className="auth-error" role="alert">
          <Icon name="alert" size={16} />
          {err}
        </div>
      ) : null}

      <form className="auth-form" onSubmit={onSubmit} noValidate>
        <CompanySearch
          label="Rechercher votre entreprise"
          help="Tapez votre SIREN ou votre nom : préremplit le nom et enregistre vos informations légales (recommandé)."
          onPick={pickCompany}
          autoFocus
        />
        <Field label="Nom de votre entreprise">
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="organization"
            placeholder="Mon Entreprise SARL"
            disabled={busy}
            required
          />
        </Field>

        {company ? (
          <>
            {!company.address ? (
              <p className="auth-sub" style={{ marginTop: -4 }}>
                Vos informations sont non diffusibles dans l’annuaire : complétez votre adresse pour permettre votre raccordement à la plateforme (déclenché à la signature du mandat).
              </p>
            ) : null}
            <Field label="Adresse">
              <Input
                type="text"
                value={addr}
                onChange={(e) => setAddr(e.target.value)}
                placeholder="24 quai Saint-Antoine"
                disabled={busy}
              />
            </Field>
            <Field label="Code postal">
              <Input
                type="text"
                inputMode="numeric"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder="69002"
                disabled={busy}
              />
            </Field>
            <Field label="Ville">
              <Input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Lyon"
                disabled={busy}
              />
            </Field>
            <Field label="Code APE / NAF" help="Les 2 premiers chiffres (ex. 62), figure sur votre avis INSEE.">
              <Input
                type="text"
                inputMode="numeric"
                maxLength={2}
                value={naf}
                onChange={(e) => setNaf(e.target.value.replace(/\D/g, '').slice(0, 2))}
                placeholder="62"
                disabled={busy}
              />
            </Field>
            {company.vatNumber ? (
              <Field
                label="N° TVA intracommunautaire"
                help="Calculé automatiquement depuis votre SIREN, rien à saisir."
              >
                <Input type="text" value={company.vatNumber} disabled />
              </Field>
            ) : null}
            <Field label="Taille de l’entreprise" help="Pour l’enregistrement e-reporting (DGFiP).">
              <select
                className="ctrl"
                value={enterpriseSize}
                onChange={(e) => setEnterpriseSize(e.target.value as typeof enterpriseSize)}
                disabled={busy}
              >
                <option value="micro">Micro-entreprise / TPE</option>
                <option value="pme">PME</option>
                <option value="eti">ETI</option>
                <option value="ge">Grande entreprise</option>
              </select>
            </Field>
            <Field label="Nature de votre activité">
              <select
                className="ctrl"
                value={typeOperation}
                onChange={(e) => setTypeOperation(e.target.value as typeof typeOperation)}
                disabled={busy}
              >
                <option value="services">Prestations de services</option>
                <option value="goods">Vente de biens</option>
                <option value="mixed">Les deux (mixte)</option>
              </select>
            </Field>
          </>
        ) : null}

        <div className="auth-actions">
          <Button type="submit" full loading={loading} disabled={busy}>
            Créer mon espace
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
